import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  TrainingReport,
  ComplianceReport,
  KpiData,
  CompetencyMatrixPosition,
  UserReport,
} from '@/types';

const KEYS = {
  training: (params?: Record<string, string | number | undefined>) =>
    ['reports', 'training', params] as const,
  compliance: (params?: Record<string, string | number | undefined>) =>
    ['reports', 'compliance', params] as const,
  kpis: (params?: Record<string, string | number | undefined>) =>
    ['reports', 'kpis', params] as const,
  matrix: (params?: Record<string, string | number | undefined>) =>
    ['reports', 'competency-matrix', params] as const,
  user: (userId: number) => ['reports', 'user', userId] as const,
};

export function useTrainingReport(params?: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: KEYS.training(params),
    queryFn: () => api.get<TrainingReport>('/reports/training', params),
    select: (res) => res.data,
    placeholderData: keepPreviousData,
  });
}

export function useComplianceReport(params?: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: KEYS.compliance(params),
    queryFn: () => api.get<ComplianceReport>('/reports/compliance', params),
    select: (res) => res.data,
    placeholderData: keepPreviousData,
  });
}

export function useKpis(params?: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: KEYS.kpis(params),
    queryFn: () => api.get<KpiData>('/reports/kpis', params),
    select: (res) => res.data,
    placeholderData: keepPreviousData,
  });
}

export function useCompetencyMatrix(params?: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: KEYS.matrix(params),
    queryFn: () => api.get<CompetencyMatrixPosition[]>('/reports/competency-matrix', params),
    select: (res) => res.data,
    placeholderData: keepPreviousData,
  });
}

export function useUserReport(userId: number) {
  return useQuery({
    queryKey: KEYS.user(userId),
    queryFn: () => api.get<UserReport>(`/reports/user/${userId}`),
    select: (res) => res.data,
    enabled: userId > 0,
  });
}
